import React, { useEffect, useState, useMemo } from "react";
import { api, downloadCsv, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import KPICard from "@/components/common/KPICard";
import { CurrencyDollar, Clock, CheckSquare, Wallet, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RewardsPayouts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/milestones"); setRows(data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const t = { pending: 0, approved: 0, paid: 0 };
    rows.forEach((m) => {
      if (m.status === "pending_verification" || m.status === "achieved") t.pending += m.reward_amount;
      else if (m.status === "approved") t.approved += m.reward_amount;
      else if (m.status === "paid") t.paid += m.reward_amount;
    });
    return t;
  }, [rows]);

  const act = async (m, status) => {
    try { await api.post(`/milestones/${m.id}/action`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Rewards & Payouts"
        subtitle="Approve and mark milestone reward payouts. Track payment status."
        breadcrumbs="Rewards / Payouts"
        testId="payouts-page"
        actions={
          <button
            data-testid="payouts-export-btn"
            onClick={async () => {
              try { await downloadCsv("/exports/payouts.csv"); toast.success("Payouts exported"); }
              catch (e) { toast.error(formatApiError(e)); }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 text-gray-200 hover:bg-white/5 text-sm"
          >
            <DownloadSimple size={14} /> Export CSV
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPICard testId="payout-pending" title="Pending Payout" value={`$${totals.pending.toFixed(2)}`} icon={<Clock size={20} weight="duotone" />} accent="orange" />
        <KPICard testId="payout-approved" title="Approved Payout" value={`$${totals.approved.toFixed(2)}`} icon={<CheckSquare size={20} weight="duotone" />} accent="blue" />
        <KPICard testId="payout-paid" title="Paid Payout" value={`$${totals.paid.toFixed(2)}`} icon={<CurrencyDollar size={20} weight="duotone" />} accent="green" />
        <KPICard testId="payout-total" title="Total Reward Pool" value={`$${(totals.pending + totals.approved + totals.paid).toFixed(2)}`} icon={<Wallet size={20} weight="duotone" />} accent="purple" />
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3 text-center">Milestone</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Paid Date</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-500">No rewards yet</td></tr> :
                rows.map((m) => (
                  <tr key={m.id} data-testid={`payout-row-${m.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <p className="text-white">{m.participant_name}</p>
                      <p className="text-xs font-mono text-gray-500">{m.account_number}</p>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-orange-300">{m.milestone_lot}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-400">${m.reward_amount.toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400">{m.paid_at ? m.paid_at.slice(0, 10) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {m.status === "pending_verification" && <button data-testid={`payout-approve-${m.id}`} onClick={() => act(m, "approved")} className="px-2 py-1 text-xs bg-blue-500/10 text-blue-300 border border-blue-500/30 rounded-md">Approve</button>}
                        {m.status === "approved" && <button data-testid={`payout-pay-${m.id}`} onClick={() => act(m, "paid")} className="px-2 py-1 text-xs bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-md">Mark Paid</button>}
                        {(m.status === "pending_verification" || m.status === "approved") && <button data-testid={`payout-hold-${m.id}`} onClick={() => act(m, "on_hold")} className="px-2 py-1 text-xs bg-gray-500/10 text-gray-300 border border-gray-500/30 rounded-md">On Hold</button>}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
